import { getMacros } from "../stores/all-products";
import type { ProductV3 } from "../types";

export const isHighProtein = (protein: number, serving: number) => {
  return (protein / serving) * 100 >= 20; // Greek yogurt.
};
export const isMediumProtein = (protein: number, serving: number, calories: number) => {
  return (protein / serving) * 100 >= 10 || ((protein / serving) * 100) / calories >= 0.08;
};

export const isHighFat = (fat: number, serving: number) => {
  return (fat / serving) * 100 >= 50;
};
export const isMediumFat = (fat: number, serving: number) => {
  return (fat / serving) * 100 >= 25; // Potato chips.
};

export const isMediumCalories = (calories: number, serving: number) => {
  return (calories / serving) * 100 >= 400 && (calories / serving) * 100 <= 600;
};

export const isHighCalories = (calories: number, serving: number) => {
  return (calories / serving) * 100 > 600; // Peanut butter.
};

export const isHighSugar = (sugar: number, serving: number) => {
  return (sugar / serving) * 100 >= 44;
};

export const isMediumSugar = (sugar: number, serving: number) => {
  return (sugar / serving) * 100 >= 22; // Donut.
};

export const isHighSaturatedFat = (saturatedFat: number, serving: number) => {
  return (saturatedFat / serving) * 100 >= 5;
};

export const isHighFiber = (fiber: number, serving: number) => {
  return (fiber / serving) * 100 >= 6;
};

export const isMediumFiber = (fiber: number, serving: number, calories: number) => {
  return (fiber / serving) * 100 < 6 && ((fiber / serving) * 100) / calories >= 0.014;
};

export const isHighSalt = (salt: number, serving: number) => {
  return (salt / serving) * 100 >= 3.0;
};

export const isMediumSalt = (salt: number, serving: number) => {
  return (salt / serving) * 100 >= 1.5;
};

export const isBreakfast = (terms: string[]) => {
  const regexp =
    /breakfast|coffee|bread|dairy|dairie|milk|fruit|cereal|yogurt|egg|toast|pancake|waffle|muffin|pastry|pie|tart|jam|jelly|syrup|honey|nut/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isLunch = (terms: string[]) => {
  const regexp = /lunch|bread|pasta|sauce|salad|burger|sandwich/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isDinner = (terms: string[]) => {
  const regexp =
    /dinner|pasta|sauce|noodle|rice|steak|vegetable|sushi|salad|pizza|burger|sandwich|fish|chicken|meat|beef|pork|shrimp|seafood/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isDrink = (terms: string[]) => {
  const regexp = /drink|water|tea|coffee|juice|soda|beer|wine|cocktail|alcohol/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isFruit = (terms: string[]) => {
  const regexp = /fruit|apple|banana|orange|pineapple|strawberry|grape|mango|peach|pear/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isVegetable = (terms: string[]) => {
  const regexp =
    /vegetable|carrot|cucumber|lettuce|spinach|broccoli|cauliflower|asparagus|zucchini|pumpkin|tomato|peas|beans|mushroom|celery/;
  return terms.some((term) => regexp.test(term)) || false;
};

export const isMainlyProtein = (proteins: number, carbs: number, fat: number) => {
  return proteins > Math.max(carbs, fat);
};

export const isMainlyCarbs = (proteins: number, carbs: number, fat: number) => {
  return carbs > Math.max(proteins, fat);
};

export const isMainlyFat = (proteins: number, carbs: number, fat: number) => {
  return fat > Math.max(proteins, carbs);
};

export const getMainMacro = (product: ProductV3) => {
  const macros = getMacros(product);
  if (isMainlyProtein(macros.proteins, macros.carbs, macros.fat)) return "proteins";
  if (isMainlyCarbs(macros.proteins, macros.carbs, macros.fat)) return "carbs";
  if (isMainlyFat(macros.proteins, macros.carbs, macros.fat)) return "fat";
  return "energy";
};

export const MacroColors = {
  energy: {
    textClass: "text-lime-500 dark:text-lime-400",
    bgClass: "bg-lime-100 dark:bg-lime-900",
    bgColor: "var(--color-lime-100)",
    borderClass: "border-lime-400 dark:border-lime-700",
  },
  fat: {
    textClass: "text-pink-500 dark:text-fuchsia-400",
    bgClass: "bg-pink-100 dark:bg-fuchsia-900",
    bgColor: "var(--color-pink-100)",
    borderClass: "border-pink-400 dark:border-fuchsia-700",
  },
  carbs: {
    textClass: "text-yellow-500 dark:text-yellow-400",
    bgClass: "bg-yellow-100 dark:bg-yellow-900",
    bgColor: "var(--color-yellow-100)",
    borderClass: "border-yellow-400 dark:border-yellow-700",
  },
  proteins: {
    textClass: "text-sky-500 dark:text-sky-400",
    bgClass: "bg-sky-100 dark:bg-sky-900",
    bgColor: "var(--color-sky-100)",
    borderClass: "border-sky-400 dark:border-sky-700",
  },
};
